import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content'];

  connectedCallback() {
    super.connectedCallback();

    const { content } = this.refs;
    if (content.firstElementChild?.children.length === 0) return;

    this.#addRepeatedItems();
    this.#duplicateContent();
    this.#setSpeed();

    // Initialize manual sliding functionality
    this.#initManualSliding();

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
    
    // Clean up manual sliding event listeners
    this.#cleanupManualSliding();
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  // Manual sliding properties
  #isDragging = false;
  #startX = 0;
  #scrollLeft = 0;
  #isAnimationDisabled = false;

  #slowDown = debounce(() => {
    if (this.#animation || this.#isAnimationDisabled) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    if (this.#isAnimationDisabled) return;
    
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  #setSpeed(value = this.#getUserSpeed() || this.#calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #getUserSpeed() {
    // Get user-defined speed from data attribute (convert milliseconds to seconds)
    const userSpeedMs = this.getAttribute('data-user-speed');
    return userSpeedMs ? parseFloat(userSpeedMs) : null;
  }

  #calculateSpeed() {
    // Fallback calculation when no user speed is set
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const marqueeWidth = this.offsetWidth;
    const speed = Math.ceil(marqueeWidth / speedFactor / 2);
    return speed;
  }

  #handleResize = debounce(() => {
    const { content } = this.refs;
    const newNumberOfCopies = this.#calculateNumberOfCopies();
    const currentNumberOfCopies = content.children.length;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed();
    
    // Reset transform position for manual sliding mode
    if (this.#isAnimationDisabled) {
      this.refs.wrapper.style.transform = 'translate3d(0px, 0, 0)';
    } else {
      this.#restartAnimation();
    }
  }, 250);

  #restartAnimation() {
    if (this.#isAnimationDisabled) return;
    
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    // Only duplicate if animation is enabled
    if (!this.#isAnimationDisabled) {
      const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));
      clone.setAttribute('aria-hidden', 'true');
      clone.removeAttribute('ref');
      this.refs.wrapper.appendChild(clone);
    }
  }

  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;
    const wrapper = content.firstElementChild;

    if (!wrapper) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = wrapper.cloneNode(true);
      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;

    for (let i = 0; i < numberOfCopies; i++) {
      content.lastElementChild?.remove();
    }
  }

  #calculateNumberOfCopies() {
    const { content } = this.refs;
    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth =
      content.firstElementChild instanceof HTMLElement ? content.firstElementChild.offsetWidth : 1;

    return marqueeRepeatedItemWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeRepeatedItemWidth);
  }

  // Manual sliding functionality
  #initManualSliding() {
    // Check if animation is disabled
    this.#checkAnimationState();
    
    // Add event listeners for manual sliding
    this.addEventListener('mousedown', this.#handleMouseDown);
    this.addEventListener('touchstart', this.#handleTouchStart, { passive: false });
    
    // Global event listeners
    document.addEventListener('mousemove', this.#handleMouseMove);
    document.addEventListener('mouseup', this.#handleMouseUp);
    document.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
    document.addEventListener('touchend', this.#handleTouchEnd);
  }

  #cleanupManualSliding() {
    this.removeEventListener('mousedown', this.#handleMouseDown);
    this.removeEventListener('touchstart', this.#handleTouchStart);
    
    document.removeEventListener('mousemove', this.#handleMouseMove);
    document.removeEventListener('mouseup', this.#handleMouseUp);
    document.removeEventListener('touchmove', this.#handleTouchMove);
    document.removeEventListener('touchend', this.#handleTouchEnd);
  }

  #checkAnimationState() {
    // Check if the disable_scroll_marquee setting is enabled
    const computedStyle = getComputedStyle(this.refs.wrapper);
    const animationName = computedStyle.animationName;
    this.#isAnimationDisabled = animationName === 'none' || !animationName || animationName === '';
    
    if (this.#isAnimationDisabled) {
      this.style.cursor = 'grab';
    }
  }

  #handleMouseDown = (e) => {
    if (!this.#isAnimationDisabled) return;
    
    e.preventDefault();
    this.#isDragging = true;
    this.#startX = e.pageX;
    this.#scrollLeft = this.#getCurrentTransform();
    this.style.cursor = 'grabbing';
  }

  #handleTouchStart = (e) => {
    if (!this.#isAnimationDisabled) return;
    
    e.preventDefault();
    this.#isDragging = true;
    this.#startX = e.touches[0].pageX;
    this.#scrollLeft = this.#getCurrentTransform();
  }

  #handleMouseMove = (e) => {
    if (!this.#isDragging || !this.#isAnimationDisabled) return;
    
    e.preventDefault();
    const x = e.pageX;
    const walk = (x - this.#startX) * 1; // Adjust scroll speed
    this.#setTransform(this.#scrollLeft + walk);
  }

  #handleTouchMove = (e) => {
    if (!this.#isDragging || !this.#isAnimationDisabled) return;
    
    e.preventDefault();
    const x = e.touches[0].pageX;
    const walk = (x - this.#startX) * 1; // Adjust scroll speed
    this.#setTransform(this.#scrollLeft + walk);
  }

  #handleMouseUp = () => {
    if (!this.#isAnimationDisabled) return;
    
    this.#isDragging = false;
    this.style.cursor = 'grab';
    this.#snapToPosition();
  }

  #handleTouchEnd = () => {
    if (!this.#isAnimationDisabled) return;
    
    this.#isDragging = false;
    this.#snapToPosition();
  }

  #getCurrentTransform() {
    const style = getComputedStyle(this.refs.wrapper);
    const transform = style.transform;
    
    if (transform === 'none') return 0;
    
    const matrix = transform.match(/matrix\(([^)]+)\)/);
    if (matrix) {
      const values = matrix[1].split(', ');
      return parseFloat(values[4]) || 0;
    }
    
    return 0;
  }

  #setTransform(value) {
    // Calculate boundaries to prevent over-scrolling
    const containerWidth = this.offsetWidth;
    
    // Get total width including cloned content
    const totalContentWidth = this.refs.wrapper.scrollWidth;
    const actualContentWidth = this.refs.content.offsetWidth;
    
    // For manual sliding, we want to show all original content plus some buffer
    // Calculate max scroll to show the last item completely
    const maxScroll = -(actualContentWidth - containerWidth);
    
    // Add some padding to ensure last item is fully visible
    const padding = 50;
    const adjustedMaxScroll = maxScroll - padding;
    
    // Clamp the value within boundaries (0 to maxScroll)
    const clampedValue = Math.max(adjustedMaxScroll, Math.min(0, value));
    
    this.refs.wrapper.style.transform = `translate3d(${clampedValue}px, 0, 0)`;
  }

  #snapToPosition() {
    if (!this.#isAnimationDisabled) return;
    
    const currentTransform = this.#getCurrentTransform();
    const containerWidth = this.offsetWidth;
    const actualContentWidth = this.refs.content.offsetWidth;
    
    // Check if we're near the end
    const maxScroll = -(actualContentWidth - containerWidth);
    const padding = 50;
    const adjustedMaxScroll = maxScroll - padding;
    
    // If we're at or past the end, snap to show last item fully
    if (currentTransform <= adjustedMaxScroll + 20) {
      this.refs.wrapper.style.transition = 'transform 0.3s ease-out';
      this.#setTransform(adjustedMaxScroll);
      
      setTimeout(() => {
        this.refs.wrapper.style.transition = '';
      }, 300);
      return;
    }
    
    // Optional: Add smooth snapping to nearest item for middle positions
    const firstItem = this.refs.content.firstElementChild?.firstElementChild;
    if (firstItem && firstItem instanceof HTMLElement) {
      const itemWidth = firstItem.offsetWidth;
      const gap = parseInt(getComputedStyle(this).getPropertyValue('--marquee-gap')) || 24;
      const itemWithGap = itemWidth + gap;
      
      if (itemWithGap > 0) {
        const snapPosition = Math.round(Math.abs(currentTransform) / itemWithGap) * itemWithGap;
        const finalPosition = -snapPosition;
        
        // Ensure we don't go past boundaries
        const clampedPosition = Math.max(adjustedMaxScroll, Math.min(0, finalPosition));
        
        // Smooth transition to snap position
        this.refs.wrapper.style.transition = 'transform 0.3s ease-out';
        this.#setTransform(clampedPosition);
        
        // Remove transition after animation
        setTimeout(() => {
          this.refs.wrapper.style.transition = '';
        }, 300);
      }
    }
  }

  // Override existing methods to handle animation state
  static get observedAttributes() {
    return ['data-speed-factor', 'data-movement-direction', 'data-user-speed'];
  }

  attributeChangedCallback(name) {
    // Re-check animation state when attributes change
    this.#checkAnimationState();
    
    // Update speed when user speed changes
    if (name === 'data-user-speed') {
      this.#setSpeed();
    }
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}